/**
 * Registry Adapters
 *
 * These adapters integrate the new template registry with the existing
 * TemplatePreviewRenderer and htmlGenerator. This allows gradual migration
 * without breaking the existing system.
 *
 * Usage:
 * 1. Import these in TemplatePreviewRenderer and htmlGenerator
 * 2. Check if template exists in registry first
 * 3. Fall back to existing switch statement if not
 */

import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { templateRegistry, getTemplate, generateTemplateHtml, generateTemplatePreviewHtml } from './index';
import { TemplateConfig, PreviewProps } from './types';

/**
 * Check if a template ID is in the new registry
 */
export const isInRegistry = (templateId: string): boolean => {
  return templateId in templateRegistry;
};

/**
 * Render preview from registry
 * Returns null if template not found (caller should fall back to switch)
 */
export const renderRegistryPreview = (
  templateId: string,
  config: TemplateConfig,
  isGalleryPreview: boolean,
  expandedLists: Record<string, boolean>,
  toggleExpand: (id: string) => void
): React.ReactNode | null => {
  const template = getTemplate(templateId);
  if (!template) return null;

  const PreviewComponent = template.Preview;
  return (
    <PreviewComponent
      config={config}
      isGalleryPreview={isGalleryPreview}
      expandedLists={expandedLists}
      toggleExpand={toggleExpand}
    />
  );
};

/**
 * Generate HTML from registry
 * Returns null if template not found (caller should fall back to switch)
 */
export const generateRegistryHtml = (templateId: string, config: TemplateConfig): string | null => {
  if (!isInRegistry(templateId)) return null;
  return generateTemplateHtml(templateId, config);
};

/**
 * Generate preview HTML from registry
 * Returns null if template not found (caller should fall back to switch)
 */
export const generateRegistryPreviewHtml = (templateId: string, config: TemplateConfig): string | null => {
  if (!isInRegistry(templateId)) return null;
  return generateTemplatePreviewHtml(templateId, config);
};

/**
 * HOC to wrap existing preview renderer with registry lookup
 */
export const withRegistryPreview = <P extends { templateId: string; config: TemplateConfig }>(
  WrappedComponent: React.ComponentType<P>
): React.FC<P & { expandedLists?: Record<string, boolean>; toggleExpand?: (id: string) => void }> => {
  return function RegistryPreviewWrapper(props) {
    const { templateId, config, expandedLists = {}, toggleExpand = () => {} } = props;

    // Check registry first
    const template = getTemplate(templateId);
    if (template) {
      const PreviewComponent = template.Preview;
      return (
        <PreviewComponent
          config={config}
          isGalleryPreview={false}
          expandedLists={expandedLists}
          toggleExpand={toggleExpand}
        />
      );
    }

    // Fall back to existing component
    return <WrappedComponent {...props} />;
  };
};
